/**
 * useSprintDetailCore Hook
 * 
 * Composes core state, sync, and action hooks for SprintDetail.
 * Groups: state, sprint sync, actions, multi-campaign sync, keyboard.
 */
import { useEffect, useRef } from 'react';
import useSprintDetailState from './useSprintDetailState';
import { useSprintSync } from './useSprintSync';
import { useSprintActions } from './useSprintActions';
import useMultiCampaignSync from './useMultiCampaignSync';
import useSprintKeyboard from './useSprintKeyboard';
import { DEFAULT_STATUSES } from '../FlowchartView/TaskNode/constants';
import { registerMissionDragContextRef, clearMissionDragContextRef } from '../../../utils/missionToSidebarBridge';

const useSprintDetailCore = ({
  sprint,
  allSprints,
  onUpdateSprint,
  activeView,
  multiCampaignData,
}) => {
  const isMultiCampaignMode = !!activeView && !!multiCampaignData;

  // Build effective sprint (real sprint or virtual multi-campaign sprint)
  const effectiveSprint = isMultiCampaignMode ? {
    id: `view-${activeView.id}`,
    name: activeView.name,
    icon: activeView.icon,
    tasks: multiCampaignData.tasks,
    statuses: DEFAULT_STATUSES,
    progress: 0,
  } : sprint;

  // All local state
  const state = useSprintDetailState();

  // Sprint sync with undo/redo
  const {
    localSprint, setLocalSprint, recordAction, wrapLastUndoAction,
    undo, redo, undoTo, redoTo, undoAll, clearHistory,
    startBatch, endBatch, undoStack, redoStack, canUndo, canRedo,
  } = useSprintSync(effectiveSprint, isMultiCampaignMode ? null : onUpdateSprint, 500, isMultiCampaignMode);

  // Task and sprint actions
  const actions = useSprintActions(localSprint, setLocalSprint, allSprints, recordAction, { 
    onUpdateSprint,
    onDateValidationError: state.setDateValidationError,
  });

  // Multi-campaign sync (propagates changes back to source campaigns)
  useMultiCampaignSync({ isMultiCampaignMode, localSprint, allSprints, onUpdateSprint });

  // Expose live sprint tasks to sidebar drag-drop (global sprints[] is often metadata-only).
  // Ref is updated every render; register once on mount so localSprint churn never nulls context.
  const dragContextRef = useRef({ localSprint: null, isMultiCampaignMode: false });
  dragContextRef.current = { localSprint, isMultiCampaignMode };

  useEffect(() => {
    registerMissionDragContextRef(dragContextRef);
    return () => clearMissionDragContextRef(dragContextRef);
  }, []);

  // Keyboard shortcuts for undo/redo
  useSprintKeyboard({ undo, redo });

  return {
    // Mode
    isMultiCampaignMode,
    effectiveSprint,
    
    // State (spread all state values)
    state,
    
    // Sprint data
    localSprint,
    setLocalSprint,
    
    // Actions
    actions,
    recordAction,
    
    // Undo/Redo
    undo, redo, undoTo, redoTo, undoAll, clearHistory,
    startBatch, endBatch, wrapLastUndoAction, undoStack, redoStack, canUndo, canRedo,
  };
};

export default useSprintDetailCore;
